import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import arrayMutators from 'final-form-arrays';
import { FORM_ERROR, type FormApi } from 'final-form';
import { Form, useField, useForm, useFormState } from 'react-final-form';
import { DragDropContext, type DropResult } from '@hello-pangea/dnd';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../../services';
import ZuulLoader from '../../../components/ZuulLoader/ZuulLoader';
import ActiveStatusModal from '../../../components/ActiveStatusModal/ActiveStatusModal';
import AuditModalButton from '../../../components/AuditModal/AuditModal';
import CommandK from '../../../components/CommandK/CommandK/CommandK';
import { UnifiedFormCriteriaPanel } from '../../../components/Criteria/CriteriaPanel/UnifiedFormCriteriaPanel';
import { validateCriteriaConfiguration } from '../../../components/Criteria/CriteriaPanel/validation';
import { formatDate } from '../../../utils/conversionFunctions';
import { getPageTitle } from '../../../utils/pageTitles';
import {
  BuilderHeader,
  ConfigPanel,
  containerPath,
  EditorSwitch,
  entityLinkFor,
  InlineNameField,
  insertVisible,
  layoutPath,
  LIBRARY_DROPPABLE_ID,
  moveStep,
  moveToEdge,
  moveVisible,
  OfferLibrary,
  OfferPopover,
  OfferStack,
  OfferVariationsList,
  PopoverFact,
  RefPickerField,
  STACK_DROPPABLE_ID,
  StackBuilderLayout,
  typeLabel,
  useUnsavedChangesGuard,
  VariationChips,
  variationsOf,
  type StackEntry,
} from '../../../features/stackBuilder';
import {
  buildOfferBlockSave,
  createBlockItem,
  emptyOfferBlock,
  loadOfferBlock,
  type BlockItem,
  type LoadedOfferBlock,
  type Offer,
} from './offerBlockModel';
import {
  useBlockContainers,
  useBlockLayouts,
  useBlockOffers,
  useBlockVariationsMap,
  useOfferBlockRecord,
} from './queries';

/* eslint-disable @typescript-eslint/no-explicit-any -- API entities */
const zuulUser = () => {
  try {
    return JSON.parse(localStorage.getItem('userData') || 'null');
  } catch {
    return null;
  }
};

const validate = (values: Record<string, any>) => {
  const errors: Record<string, any> = {};
  if (!values.offer?.name?.trim()) errors.offer = { name: 'Add an offer block name.' };
  const criteriaConfiguration = values?.criteria?.criteriaConfiguration;
  if (criteriaConfiguration) {
    const result = validateCriteriaConfiguration(criteriaConfiguration);
    if (result.hasErrors) errors.criteria = result.summary || 'The criteria have errors.';
  }
  return errors;
};

const chipSx = { height: 18, fontSize: '0.6875rem' };

function Workbench({
  offerBlockId,
  loaded,
  copyRef,
  activeRef,
}: {
  offerBlockId: number | null;
  loaded: LoadedOfferBlock;
  copyRef: React.MutableRefObject<string | null>;
  activeRef: React.MutableRefObject<boolean>;
}) {
  const form = useForm();
  const { dirty, submitting, submitFailed, submitError, hasValidationErrors, errors } = useFormState({
    subscription: {
      dirty: true,
      submitting: true,
      submitFailed: true,
      submitError: true,
      hasValidationErrors: true,
      errors: true,
    },
  });
  const { input: stackInput } = useField<BlockItem[]>('stack', { subscription: { value: true } });
  const stack = React.useMemo(() => (Array.isArray(stackInput.value) ? stackInput.value : []), [stackInput.value]);
  const { input: nameInput, meta: nameMeta } = useField<string>('offer.name', {
    subscription: { value: true, error: true, touched: true },
  });

  const { data: offers, isLoading: offersLoading } = useBlockOffers();
  const { data: layouts } = useBlockLayouts();
  const { data: containers } = useBlockContainers();
  const { data: variationsMap } = useBlockVariationsMap();

  const isEditMode = offerBlockId != null;
  const offerId = loaded.block?.offer?.id as number | undefined;
  const [isActive, setIsActive] = React.useState<boolean>(isEditMode ? Boolean(loaded.block?.offer?.isActive) : true);
  activeRef.current = isActive;
  const [popover, setPopover] = React.useState<{ key: string; anchor: HTMLElement } | null>(null);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [copyOpen, setCopyOpen] = React.useState(false);
  const visibleKeysRef = React.useRef<string[]>([]);
  const listedRef = React.useRef<Offer[]>([]);
  const onListedChange = React.useCallback((listed: Offer[]) => {
    listedRef.current = listed;
  }, []);
  const guard = useUnsavedChangesGuard(dirty && !submitting);

  const offersById = React.useMemo(() => new Map((offers ?? []).map((offer) => [offer.id, offer])), [offers]);
  const fresh = React.useCallback((item: BlockItem) => offersById.get(item.offer.id) ?? item.offer, [offersById]);
  const setStack = (next: BlockItem[]) => form.change('stack', next);

  const addOffer = (offer: Offer, visibleIndex?: number) => {
    const latest = (form.getState().values.stack ?? []) as BlockItem[];
    const item = createBlockItem(offer);
    setStack(
      visibleIndex === undefined
        ? [...latest, item]
        : insertVisible(latest, visibleKeysRef.current, (row) => row.key, item, visibleIndex)
    );
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || destination.droppableId !== STACK_DROPPABLE_ID) return;
    if (source.droppableId === LIBRARY_DROPPABLE_ID) {
      const offer = listedRef.current[source.index];
      if (offer) addOffer(offer, destination.index);
      return;
    }
    setStack(moveVisible(stack, visibleKeysRef.current, (row) => row.key, source.index, destination.index));
  };

  const entries: StackEntry[] = React.useMemo(
    () =>
      stack.map((item) => {
        const placements = stack.filter((other) => other.offer.id === item.offer.id).length;
        const overrides = [item.layoutOverride && 'Layout', item.containerOverride && 'Container'].filter(Boolean);
        const offer = fresh(item);
        return {
          key: item.key,
          offer,
          hasOverride: overrides.length > 0,
          badges: (
            <>
              <VariationChips variations={variationsOf(offer, variationsMap)} />
              {overrides.length > 0 && (
                <Chip
                  size='small'
                  color='info'
                  variant='outlined'
                  label={`${overrides.join(' and ')} override`}
                  sx={chipSx}
                />
              )}
              {placements > 1 && (
                <Chip
                  size='small'
                  color='warning'
                  variant='outlined'
                  label={`Placed ${placements} times`}
                  sx={chipSx}
                />
              )}
            </>
          ),
        };
      }),
    [stack, fresh, variationsMap]
  );

  const popoverIndex = popover ? stack.findIndex((item) => item.key === popover.key) : -1;
  const popoverItem = popoverIndex >= 0 ? stack[popoverIndex] : undefined;
  const removeKey = (key: string) => {
    setStack(stack.filter((item) => item.key !== key));
    if (popover?.key === key) setPopover(null);
  };

  const toggleActive = (next: boolean) => {
    if (!offerId) return;
    api
      .put(`offers/active/${offerId}`, { id: offerId, isActive: next, zuulUser: zuulUser() })
      .then(() => setIsActive(next));
  };

  const publish = () => {
    if (hasValidationErrors) {
      void form.submit();
      return;
    }
    setPublishOpen(true);
  };

  const block = loaded.block;
  const meta = (
    <>
      {submitError ? (
        <Typography variant='caption' color='error'>
          {submitError}
        </Typography>
      ) : submitFailed && hasValidationErrors ? (
        <Typography variant='caption' color='error'>
          {`Fix ${Object.keys(errors ?? {}).length === 1 ? 'the highlighted field' : 'the highlighted fields'} before publishing.`}
        </Typography>
      ) : block?.publishedAt ? (
        <Typography variant='caption' color='text.secondary' noWrap>
          {`Published ${formatDate(block.publishedAt)}${block?.zuulUser?.realname ? ` by ${block.zuulUser.realname}` : ''}`}
        </Typography>
      ) : (
        <Typography variant='caption' color='text.secondary'>
          Not published yet
        </Typography>
      )}
      {dirty && (
        <Typography variant='caption' color='warning.main'>
          Unpublished changes
        </Typography>
      )}
    </>
  );

  const popoverOffer = popoverItem ? fresh(popoverItem) : null;
  const popoverContainers = containers?.filter((container) => container.offerTypeHint === popoverOffer?.offerType?.id);

  return (
    <>
      <Helmet>
        <title>{getPageTitle(window.location.pathname, nameInput.value)}</title>
      </Helmet>
      <DragDropContext onDragEnd={onDragEnd}>
        <StackBuilderLayout
          header={
            <BuilderHeader
              isActive={isActive}
              backTo='/campaigns/offerblocks'
              backLabel='Offer Blocks'
              name={
                <InlineNameField
                  value={nameInput.value}
                  onChange={nameInput.onChange}
                  onBlur={() => nameInput.onBlur()}
                  label='Offer block name'
                  placeholder='Name this offer block'
                  error={nameMeta.error}
                  showError={Boolean(nameMeta.touched || submitFailed)}
                />
              }
              identifier={isEditMode ? `ID ${offerBlockId}` : 'New offer block'}
              status={
                offerId ? (
                  <ActiveStatusModal
                    framed
                    isActive={isActive}
                    modalType='offer block'
                    content={{ name: nameInput.value }}
                    onClickSubmit={toggleActive}
                  />
                ) : undefined
              }
              actions={
                <>
                  <EditorSwitch screen='offerBlock' confirmLoss={dirty} />
                  <AuditModalButton />
                  {isEditMode && (
                    <Button size='small' variant='outlined' onClick={() => setCopyOpen(true)} disabled={submitting}>
                      Copy
                    </Button>
                  )}
                  <Button size='small' variant='contained' onClick={publish} disabled={submitting}>
                    {submitting ? 'Publishing' : 'Publish'}
                  </Button>
                  <CommandK hide />
                </>
              }
              meta={meta}
            />
          }
          library={
            <OfferLibrary
              offers={offers ?? []}
              loading={offersLoading}
              kinds={['offers']}
              onAdd={(offer) => addOffer(offer)}
              onListedChange={onListedChange}
            />
          }
          stack={
            <OfferStack
              title='Block'
              entries={entries}
              visibleKeysRef={visibleKeysRef}
              selectedKey={popover?.key}
              onOpen={(key, anchor) => setPopover({ key, anchor })}
              onRemove={removeKey}
              onMoveToEdge={(key, edge) =>
                setStack(
                  moveToEdge(
                    stack,
                    stack.findIndex((item) => item.key === key),
                    edge
                  )
                )
              }
              onMoveStep={(key, delta) =>
                setStack(moveStep(stack, visibleKeysRef.current, (row) => row.key, key, delta))
              }
              emptyText='Drag offers here from the library, or press + on one, to build the block.'
              removeLabel='Remove from block'
              legend={['inactive', 'override']}
            />
          }
          config={
            <ConfigPanel
              label='Offer block configuration'
              tabs={[
                {
                  value: 'criteria',
                  label: 'Criteria',
                  hasError: Boolean(submitFailed && errors?.criteria),
                  // A block's criteria belong to its parent offer, hence `offer`; the same shared panel, unchanged.
                  content: <UnifiedFormCriteriaPanel name='criteria' sourceType='offer' hideCapsTab />,
                },
              ]}
            />
          }
        />
      </DragDropContext>

      {popoverItem && popoverOffer && (
        <OfferPopover
          anchorEl={popover?.anchor ?? null}
          onClose={() => setPopover(null)}
          title={popoverOffer.name}
          meta={
            <>
              <Typography variant='caption'>{`Position ${popoverIndex + 1}`}</Typography>
              <Typography variant='caption'>{`ID ${popoverOffer.id}`}</Typography>
              <Chip size='small' label={typeLabel(popoverOffer)} sx={chipSx} />
              {!popoverOffer.isActive && (
                <Chip size='small' color='error' variant='outlined' label='Inactive' sx={chipSx} />
              )}
            </>
          }
          openLink={{ to: entityLinkFor(popoverOffer).to, label: 'Open offer' }}
          onRemove={() => removeKey(popoverItem.key)}
          removeLabel='Remove from block'
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <PopoverFact label='Offer type'>{typeLabel(popoverOffer) || '—'}</PopoverFact>
            <PopoverFact label='Advertiser'>{popoverOffer.advertiser?.name ?? 'None'}</PopoverFact>
          </Box>
          <OfferVariationsList variations={variationsOf(popoverOffer, variationsMap)} />
          <RefPickerField
            name={`stack[${popoverIndex}].layoutOverride`}
            label='Layout override'
            placeholder="Use the offer's own layout"
            options={layouts}
            linkTo={(layout) => layoutPath(layout.id)}
          />
          <RefPickerField
            name={`stack[${popoverIndex}].containerOverride`}
            label='Container override'
            placeholder="Use the offer's own container"
            options={popoverContainers}
            linkTo={(container) => containerPath(container.id)}
          />
          {stack.filter((item) => item.offer.id === popoverOffer.id).length > 1 && (
            <Typography variant='caption' color='text.secondary'>
              This offer is placed more than once; every placement shares one override.
            </Typography>
          )}
        </OfferPopover>
      )}

      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)}>
        <DialogTitle>Publish this offer block?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The published version is replaced with what you see here, and goes live straight away.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={() => {
              setPublishOpen(false);
              copyRef.current = null;
              void form.submit();
            }}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      <CopyDialog
        open={copyOpen}
        defaultName={`${nameInput.value} copy`}
        onClose={() => setCopyOpen(false)}
        onCopy={(name) => {
          setCopyOpen(false);
          copyRef.current = name;
          void Promise.resolve(form.submit()).finally(() => {
            copyRef.current = null;
          });
        }}
      />
      {guard}
    </>
  );
}

function CopyDialog({
  open,
  defaultName,
  onClose,
  onCopy,
}: {
  open: boolean;
  defaultName: string;
  onClose: () => void;
  onCopy: (name: string) => void;
}) {
  const [name, setName] = React.useState(defaultName);
  const [tried, setTried] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setName(defaultName);
      setTried(false);
    }
  }, [open, defaultName]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle>Copy this offer block</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The copy is a new offer block with this one's offers and criteria, including changes you have not published.
        </DialogContentText>
        <TextField
          label='Name of the copy'
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={tried && !name.trim()}
          helperText={tried && !name.trim() ? 'Name the copy.' : undefined}
          fullWidth
          size='small'
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant='contained'
          onClick={() => {
            setTried(true);
            if (name.trim()) onCopy(name.trim());
          }}
        >
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The stack builder for offer blocks: the offer library, the block's offers as an ordered stack, and
 * its criteria. Saves what the classic editor saves (see offerBlockModel).
 */
export default function OfferBlockBuilder() {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const offerBlockId =
    params.offerBlockId && params.offerBlockId !== 'create' ? parseInt(params.offerBlockId, 10) : null;
  const { data: record, isLoading, isError } = useOfferBlockRecord(offerBlockId);
  const copyRef = React.useRef<string | null>(null);
  const activeRef = React.useRef<boolean>(true);

  const loaded = React.useMemo<LoadedOfferBlock | null>(() => {
    if (offerBlockId == null) return loadOfferBlock(emptyOfferBlock());
    return record ? loadOfferBlock(record) : null;
  }, [offerBlockId, record]);

  if (isError) {
    return (
      <Alert severity='error' sx={{ m: 2 }}>
        This offer block could not be loaded. Reload the page, or switch to the classic editor.
      </Alert>
    );
  }
  if ((offerBlockId != null && isLoading) || !loaded) return <ZuulLoader text='Loading offer block' />;

  const onSubmit = async (values: Record<string, any>, formApi: FormApi) => {
    const { offer, block, isCreate } = buildOfferBlockSave(values, {
      offerBlockId,
      copyName: copyRef.current,
      isActive: activeRef.current,
      savedEntries: loaded.savedEntries,
      zuulUser: zuulUser(),
    });
    try {
      if (isCreate) {
        await api.post('offerblocks', block);
      } else {
        await api.put(`offers/${offer.id}`, offer);
        await api.put(`offerblocks/${offerBlockId}`, block);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['offerBlocks'] }),
        queryClient.invalidateQueries({ queryKey: ['offersTable'] }),
      ]);
      formApi.reset(values);
      navigate('/campaigns/offerblocks');
      return undefined;
    } catch (error: any) {
      return {
        [FORM_ERROR]:
          error?.response?.data?.message ||
          'The offer block could not be published. Try again, or contact a developer if it keeps failing.',
      };
    }
  };

  return (
    <Form
      initialValues={loaded.values}
      onSubmit={onSubmit}
      validate={validate}
      mutators={{ ...arrayMutators }}
      subscription={{}}
    >
      {() => <Workbench offerBlockId={offerBlockId} loaded={loaded} copyRef={copyRef} activeRef={activeRef} />}
    </Form>
  );
}
